// ============================================================
// 20. DailyRevenuePage.tsx
// ============================================================
// src/features/reports/pages/DailyRevenuePage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getDailyRevenue, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    DollarSign, TrendingUp, TrendingDown, Calendar,
    BarChart3, ShoppingCart, Target, Zap,
    Banknote, CreditCard, Building2, ArrowUpRight
} from 'lucide-react';
import { motion } from 'framer-motion';

export function DailyRevenuePage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-daily-revenue', tenantId, params],
        queryFn: () => getDailyRevenue(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'daily_revenue', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const dailyData = data?.daily || [];
    const weekdayAnalysis = data?.weekdayAnalysis || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <ReportHeader
                title="Daily Revenue"
                description="Day-by-day revenue analysis"
                icon={Calendar}
                iconGradient="from-emerald-500 to-teal-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || dailyData.length === 0 ? (
                    <EmptyState
                        icon={<Calendar className="w-10 h-10" />}
                        title="No Revenue Data"
                        description="No daily revenue found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Days"
                                value={String(data.summary?.totalDays || 0)}
                                icon={Calendar}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Orders"
                                value={formatNumber(data.summary?.totalOrders || 0)}
                                icon={ShoppingCart}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Avg Daily Revenue"
                                value={formatNaira(data.summary?.avgDailyRevenue || 0)}
                                icon={Target}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Total Profit"
                                value={formatNaira(data.summary?.totalProfit || 0)}
                                icon={TrendingUp}
                                gradient="from-green-500 to-emerald-600"
                            />
                        </div>

                        {/* Best & Worst Day Highlights */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.summary?.bestDay && (
                                <Card className="border-emerald-200 bg-emerald-50/30">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 rounded-xl bg-emerald-100">
                                                <TrendingUp className="w-6 h-6 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-semibold">Best Day</p>
                                                <p className="text-lg font-bold text-slate-900">
                                                    {new Date(data.summary.bestDay.date).toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long' })}
                                                </p>
                                                <p className="text-2xl font-black text-emerald-600 mt-1">
                                                    {formatNaira(data.summary.bestDay.revenue)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {data.summary?.worstDay && (
                                <Card className="border-rose-200 bg-rose-50/30">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 rounded-xl bg-rose-100">
                                                <TrendingDown className="w-6 h-6 text-rose-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-semibold">Slowest Day</p>
                                                <p className="text-lg font-bold text-slate-900">
                                                    {new Date(data.summary.worstDay.date).toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long' })}
                                                </p>
                                                <p className="text-2xl font-black text-rose-600 mt-1">
                                                    {formatNaira(data.summary.worstDay.revenue)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Weekday Analysis */}
                        {weekdayAnalysis.length > 0 && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4">Weekday Performance</h3>
                                    <div className="grid grid-cols-7 gap-3">
                                        {weekdayAnalysis.map((day: any, i: number) => (
                                            <div key={i} className="text-center">
                                                <p className="text-xs font-medium text-slate-500 mb-1">{day.day.substring(0, 3)}</p>
                                                <div className="bg-slate-100 rounded-lg h-20 relative overflow-hidden">
                                                    <motion.div
                                                        initial={{ height: 0 }}
                                                        animate={{ height: `${Math.min((day.revenue / Math.max(...weekdayAnalysis.map((d: any) => d.revenue))) * 100, 100)}%` }}
                                                        transition={{ duration: 0.5, delay: i * 0.05 }}
                                                        className="absolute bottom-0 w-full bg-gradient-to-t from-emerald-500 to-teal-400 rounded-b-lg"
                                                    />
                                                </div>
                                                <p className="text-xs font-bold text-slate-700 mt-1">{formatNaira(day.avgRevenue)}</p>
                                                <p className="text-xs text-slate-400">{day.orders} orders</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Revenue Trend Chart */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4 text-emerald-500" />
                                    Revenue Trend (Running Total)
                                </h3>
                                <div className="space-y-2">
                                    {dailyData.slice(0, 30).map((day: any, i: number) => {
                                        const maxRevenue = Math.max(...dailyData.map((d: any) => d.revenue), 1);
                                        const percentage = (day.revenue / maxRevenue * 100);

                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-xs text-slate-500 w-24">
                                                    {new Date(day.date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}
                                                </span>
                                                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.3, delay: i * 0.01 }}
                                                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-500"
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 w-28 text-right">
                                                    {formatNaira(day.revenue)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Orders</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cash</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Transfer</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Card</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Margin</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Running Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {dailyData.map((day: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                                        <span className="text-sm font-medium text-slate-900">
                                                            {new Date(day.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm font-semibold">
                                                    {day.orders}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                    {formatNaira(day.revenue)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Banknote className="w-3 h-3 text-emerald-500" />
                                                        <span>{formatNaira(day.cash)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Building2 className="w-3 h-3 text-purple-500" />
                                                        <span>{formatNaira(day.transfer)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <CreditCard className="w-3 h-3 text-blue-500" />
                                                        <span>{formatNaira(day.card)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                    {formatNaira(day.cost)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold">
                                                    <span className={parseFloat(day.profit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                                        {formatNaira(day.profit)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge
                                                        variant={parseFloat(day.margin) > 20 ? 'success' : parseFloat(day.margin) > 10 ? 'warning' : 'danger'}
                                                        size="sm"
                                                    >
                                                        {day.margin}%
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-emerald-600">
                                                    {formatNaira(day.runningTotal)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}